import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from "react-router-dom";
import { Layout } from "@/shared/components/layout/Layout";
import { SearchProvider } from "@/contexts/SearchContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { NavigationProvider } from "@/contexts/NavigationContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { HomeRedirect } from "@/components/routing/HomeRedirect";
import { BusinessGuard } from "@/components/auth/BusinessGuard";
import { RouteErrorBoundary } from "@/components/error/RouteErrorBoundary";
import { runAllMigrations } from "@/lib/localStorage/migration";
import { Loader2 } from "lucide-react";
// Platform pages (Super Admin only)
import { PlatformGuard } from "./features/platform/components/PlatformGuard";
import { ObservabilityGuard } from "./features/platform/components/ObservabilityGuard";
// Firm pages (Firm Owner only)
import { FirmOwnerGuard } from "./features/firm/components/FirmOwnerGuard";

// Route-level lazy loading keeps the initial chunk small.
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const InviteAccept = lazy(() => import("./pages/InviteAccept"));
const Dashboard = lazy(() => import("./features/dashboard/pages/Dashboard"));
const Assistants = lazy(() => import("./features/dashboard/pages/Assistants"));
const CreateProcess = lazy(() => import("./features/arbitration/pages/CreateProcess"));
const Pipeline = lazy(() => import("./features/arbitration/pages/Pipeline"));
const ProcessDetail = lazy(() => import("./features/arbitration/pages/ProcessDetail"));
const Calendar = lazy(() => import("./features/calendar/pages/Calendar"));
const TemplateHub = lazy(() => import("./features/templates/pages/TemplateHub"));
const TemplateEntry = lazy(() => import("./features/templates/pages/TemplateEntry"));
const TemplateFamilyDetail = lazy(() => import("./features/templates/pages/TemplateFamilyDetail"));
const ArbitrationBusinessTemplates = lazy(() => import("./features/templates/pages/ArbitrationBusinessTemplates"));
const SuitFilingCategoryTemplates = lazy(() => import("./features/templates/pages/SuitFilingCategoryTemplates"));
const Courts = lazy(() => import("./features/courts/pages/Courts"));
const ConciliationDashboard = lazy(() => import("./features/conciliation/pages/ConciliationDashboard"));
const ConciliationCommandCenter = lazy(() => import("./features/conciliation/pages/ConciliationCommandCenter"));
const ConciliationJobSummary = lazy(() => import("./features/conciliation/pages/ConciliationJobSummary"));
const ConciliationDocumentReview = lazy(() => import("./features/conciliation/pages/ConciliationDocumentReview"));
const SuitCategories = lazy(() => import("./features/suit-filing/pages/SuitCategories"));
const StartSuitFiling = lazy(() => import("./features/suit-filing/pages/StartSuitFiling"));
const SuitProcessDetail = lazy(() => import("./features/suit-filing/pages/SuitProcessDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PlatformDashboard = lazy(() => import("./features/platform/pages/PlatformDashboard"));
const PlatformFirms = lazy(() => import("./features/platform/pages/PlatformFirms"));
const PlatformFirmDetail = lazy(() => import("./features/platform/pages/PlatformFirmDetail"));
const PlatformAdmins = lazy(() => import("./features/platform/pages/PlatformAdmins"));
const PlatformBusinesses = lazy(() => import("./features/platform/pages/PlatformBusinesses"));
const PlatformConfiguration = lazy(() => import("./features/platform/pages/PlatformConfiguration"));
const ObservabilityDashboard = lazy(() => import("./features/platform/pages/ObservabilityDashboard"));
const RateLimitSettings = lazy(() => import("./features/platform/pages/RateLimitSettings"));
const FirmOnboarding = lazy(() => import("./features/firm/pages/FirmOnboarding"));
const OrganizationHub = lazy(() => import("./features/organization/pages/OrganizationHub"));
const ActiveBusinessSettings = lazy(() => import("./features/settings/pages/ActiveBusinessSettings"));
const LetterheadComponentsList = lazy(() => import("./features/letterhead/pages/LetterheadComponentsList"));
const SignaturesPage = lazy(() => import("./features/signatures/pages/SignaturesPage"));
const WorkspacesPage = lazy(() => import("./features/workspaces/pages/WorkspacesPage"));
const VariantEditor = lazy(() => import("./features/templates/pages/VariantEditor"));
const LetterheadComponentEditor = lazy(() => import("./features/letterhead/pages/LetterheadComponentEditor"));
const OCRTool = lazy(() => import("./features/suit-filing/pages/OCRTool"));
const SelectFirm = lazy(() => import("./pages/SelectFirm"));

// Redirect helper for /businesses/:id -> /organization?tab=businesses&id=:id
function BusinessDetailRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/organization?tab=businesses&id=${id}`} replace />;
}

// Redirect /firm?tab=X&id=Y -> /organization?tab=X&id=Y (preserves all query params)
function FirmRedirect({ defaultTab }: { defaultTab: string }) {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab') || defaultTab;
  const params = new URLSearchParams(searchParams);
  params.set('tab', tab);
  return <Navigate to={`/organization?${params.toString()}`} replace />;
}

// Page loader fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: (failureCount, error: unknown) => {
        const status = typeof error === 'object' && error !== null && 'status' in error
          ? Number((error as { status?: unknown }).status)
          : null;
        if (status !== null && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

const App = () => {
  useEffect(() => {
    runAllMigrations();
  }, []);

  return (
    <AuthProvider>
      <SearchProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <NavigationProvider>
              <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/invite/accept" element={<InviteAccept />} />
                
                {/* Firm selection (protected, no sidebar/layout) */}
                <Route path="/select-firm" element={
                  <ProtectedRoute skipFirmCheck>
                    <SelectFirm />
                  </ProtectedRoute>
                } />

                {/* Protected routes */}
                <Route element={
                  <ProtectedRoute>
                    <RouteErrorBoundary>
                      <Layout />
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                }>
                  {/* Role-aware home redirect */}
                  <Route path="/" element={<HomeRedirect />} />
                  
                  {/* Business routes */}
                  <Route path="/dashboard" element={<BusinessGuard><Dashboard /></BusinessGuard>} />
                  <Route path="/assistants" element={<BusinessGuard><Assistants /></BusinessGuard>} />
                  <Route path="/assistants/create" element={<BusinessGuard><CreateProcess /></BusinessGuard>} />
                  <Route path="/pipeline" element={<BusinessGuard><Pipeline /></BusinessGuard>} />
                  <Route path="/process/:id" element={<BusinessGuard><ProcessDetail /></BusinessGuard>} />
                  <Route path="/calendar" element={<BusinessGuard><Calendar /></BusinessGuard>} />
                  <Route path="/templates" element={<BusinessGuard><TemplateHub /></BusinessGuard>} />
                  <Route path="/templates/:templateId" element={<BusinessGuard><TemplateEntry /></BusinessGuard>} />
                  <Route path="/templates/arbitration/business/:businessId" element={<BusinessGuard><ArbitrationBusinessTemplates /></BusinessGuard>} />
                  <Route path="/templates/suit-filing/category/:categoryId" element={<BusinessGuard><SuitFilingCategoryTemplates /></BusinessGuard>} />
                  <Route path="/templates/:assistant/:familyId" element={<BusinessGuard><TemplateFamilyDetail /></BusinessGuard>} />
                  <Route path="/templates/:assistant/:familyId/variant/:variantId" element={<BusinessGuard><VariantEditor /></BusinessGuard>} />
                  <Route path="/courts" element={<BusinessGuard><Courts /></BusinessGuard>} />
                  <Route path="/conciliation/dashboard" element={<BusinessGuard><ConciliationDashboard /></BusinessGuard>} />
                  <Route path="/conciliation/command-center" element={<BusinessGuard><ConciliationCommandCenter /></BusinessGuard>} />
                  <Route path="/conciliation" element={<Navigate to="/conciliation/dashboard" replace />} />
                  <Route path="/conciliation/config" element={<Navigate to="/conciliation/command-center" replace />} />
                  <Route path="/conciliation/job/:id" element={<BusinessGuard><ConciliationJobSummary /></BusinessGuard>} />
                  <Route path="/conciliation/job/:id/documents" element={<BusinessGuard><ConciliationDocumentReview /></BusinessGuard>} />
                  <Route path="/conciliation/start" element={<Navigate to="/conciliation/command-center" replace />} />
                  <Route path="/conciliation/history" element={<Navigate to="/conciliation/dashboard" replace />} />
                  <Route path="/conciliation/process/:id" element={<Navigate to="/conciliation/dashboard" replace />} />
                  <Route path="/suit-categories" element={<BusinessGuard><SuitCategories /></BusinessGuard>} />
                  <Route path="/suit-filing" element={<Navigate to="/suit-filing/start" replace />} />
                  <Route path="/suit-filing/start" element={<BusinessGuard><StartSuitFiling /></BusinessGuard>} />
                  <Route path="/suit-filing/process/:id" element={<BusinessGuard><SuitProcessDetail /></BusinessGuard>} />
                  <Route path="/ocr-tool" element={<BusinessGuard><OCRTool /></BusinessGuard>} />
                  
                  {/* Team Hub */}
                  <Route path="/organization" element={<BusinessGuard><OrganizationHub /></BusinessGuard>} />
                  <Route path="/team" element={<Navigate to="/organization?tab=members" replace />} />
                  
                  {/* Business Settings Hub */}
                  <Route path="/business/settings" element={<BusinessGuard><ActiveBusinessSettings /></BusinessGuard>} />
                  
                  {/* Settings redirects to new locations */}
                  <Route path="/settings" element={<Navigate to="/business/settings" replace />} />
                  <Route path="/settings/members" element={<Navigate to="/organization?tab=members" replace />} />
                  <Route path="/settings/business" element={<Navigate to="/business/settings" replace />} />
                  
                  {/* Letterhead Components */}
                  <Route path="/letterhead-components" element={<BusinessGuard><LetterheadComponentsList /></BusinessGuard>} />
                  <Route path="/letterhead-components/new" element={<BusinessGuard><LetterheadComponentEditor /></BusinessGuard>} />
                  <Route path="/letterhead-components/:id/edit" element={<BusinessGuard><LetterheadComponentEditor /></BusinessGuard>} />
                  <Route path="/letterhead" element={<Navigate to="/letterhead-components" replace />} />
                  
                  {/* Signatures */}
                  <Route path="/settings/signatures" element={<BusinessGuard><SignaturesPage /></BusinessGuard>} />
                  
                  {/* Workspaces */}
                  <Route path="/workspaces" element={<BusinessGuard><WorkspacesPage /></BusinessGuard>} />
                  
                  {/* Firm Hub redirects to Organization */}
                  <Route path="/firm" element={<FirmRedirect defaultTab="profile" />} />
                  <Route path="/firm/settings" element={<Navigate to="/organization?tab=settings" replace />} />
                  <Route path="/firm/owners" element={<Navigate to="/organization?tab=owners" replace />} />
                  <Route path="/firm/onboarding" element={<FirmOwnerGuard><FirmOnboarding /></FirmOwnerGuard>} />
                  
                  {/* Business redirects to Firm Hub */}
                  <Route path="/businesses" element={<Navigate to="/organization?tab=businesses" replace />} />
                  <Route path="/businesses/:id" element={<BusinessDetailRedirect />} />
                  
                  {/* Platform routes (Super Admin only) */}
                  <Route path="/platform" element={<PlatformGuard><PlatformDashboard /></PlatformGuard>} />
                  <Route path="/platform/firms" element={<PlatformGuard><PlatformFirms /></PlatformGuard>} />
                  <Route path="/platform/firms/:id" element={<PlatformGuard><PlatformFirmDetail /></PlatformGuard>} />
                  <Route path="/platform/admins" element={<PlatformGuard><PlatformAdmins /></PlatformGuard>} />
                  <Route path="/platform/businesses" element={<PlatformGuard><PlatformBusinesses /></PlatformGuard>} />
                  <Route path="/platform/configuration" element={<PlatformGuard><PlatformConfiguration /></PlatformGuard>} />
                  <Route path="/platform/queue" element={<ObservabilityGuard><ObservabilityDashboard /></ObservabilityGuard>} />
                  <Route path="/platform/rate-limits" element={<ObservabilityGuard><RateLimitSettings /></ObservabilityGuard>} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              </NavigationProvider>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </SearchProvider>
    </AuthProvider>
  );
};

export default App;
